import React, { ReactNode, useEffect, useState } from 'react';
import Modal from './Modal';
import { fetchAppStatus, IN_DEVELOPMENT_STATUS } from '../../services/appStatus';
import { isBannerDismissed, rememberBannerDismissal } from './inDevelopmentDismissal';
import './InDevelopmentBanner.scss';

interface InDevelopmentBannerProps {
  /** The app's URL slug, which is the `appToken` of its row in the App collection. */
  appToken?: string;
}

/**
 * Warns that the app on screen carries the `inDevelopment` status - meaning no
 * clinician has validated it yet - and explains what that means behind "עוד...".
 *
 * Renders nothing at all when the app is validated, when no app owns the slug,
 * when the status could not be read, or while the status is still in flight, so
 * a normal app never flashes a red bar. The status is fetched in an effect,
 * which React runs after the first paint: the app renders and is usable whether
 * the request is slow, fails, or never answers. Dismissal lasts three days; see
 * inDevelopmentDismissal.ts.
 */
const BannerContent: React.FC<InDevelopmentBannerProps> = ({ appToken }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    let isCurrent = true;
    setIsVisible(false);
    setIsModalOpen(false);

    if (!appToken) return;

    try {
      fetchAppStatus(appToken)
        .then((status) => {
          if (!isCurrent) return;
          setIsVisible(status === IN_DEVELOPMENT_STATUS && !isBannerDismissed(appToken));
        })
        .catch(() => {
          // Unreachable in practice - fetchAppStatus resolves its own failures to
          // null - but a banner must never surface as an unhandled rejection.
        });
    } catch {
      // Nor as a throw. No status means no banner, and the app is untouched.
    }

    return () => {
      // The visitor moved to another app before the status arrived.
      isCurrent = false;
    };
  }, [appToken]);

  const handleDismiss = () => {
    if (appToken) rememberBannerDismissal(appToken);
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <>
      <div className="in-development-banner" role="status">
        <span className="in-development-banner__message">
          האפליקציה בפיתוח, ולא אושרה עדיין לשימוש.
        </span>
        <button
          type="button"
          className="in-development-banner__more"
          onClick={() => setIsModalOpen(true)}
        >
          עוד...
        </button>
        <button
          type="button"
          className="in-development-banner__close"
          aria-label="סגירת ההודעה"
          onClick={handleDismiss}
        >
          &times;
        </button>
      </div>

      <Modal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        title="אפליקציה בפיתוח"
        isDismissedOnOutsideClick
        primaryButton={{ text: 'סגור' }}
      >
        <div className="in-development-modal">
          <p>
            כל אפליקציה שפותחה בפלטפורמה זו, תוקפה ואושרה לשימוש ע"י איש צוות רפואי. כשהאפליקציה
            מסומנת "בפיתוח" זה אומר שהאפליקציה עדיין לא אושרה לשימוש רשמי באף בית חולים.
          </p>
          <p>כל שימוש באפליקציה בשלב זה הוא לצורך התרשמות בלבד.</p>
          <p>
            במידה והאפליקציה נראית לך מוכנה לשימוש, ניתן ליצור איתנו קשר ונעדכן את הסטטוס של
            האפליקציה. במידה והינך רוצה להתאים את האפליקציה למחלקה שלך - נשמח לעזור.
          </p>
          <p>
            ניתן ליצור קשר עם צוות mashlom.me באימייל{' '}
            <a href="mailto:mashlom.me@gmail.com">mashlom.me@gmail.com</a>.
          </p>
        </div>
      </Modal>
    </>
  );
};

/**
 * The banner renders as a sibling of the app itself, and this client has no
 * error boundary above it - so without one here, a throw while rendering the
 * warning would blank the clinical tool underneath it. A failed warning drops
 * itself and leaves the app alone.
 */
class BannerBoundary extends React.Component<{ children: ReactNode }, { hasFailed: boolean }> {
  state = { hasFailed: false };

  static getDerivedStateFromError() {
    return { hasFailed: true };
  }

  componentDidCatch(error: unknown) {
    console.error('In-development banner failed to render', error);
  }

  render() {
    return this.state.hasFailed ? null : this.props.children;
  }
}

const InDevelopmentBanner: React.FC<InDevelopmentBannerProps> = (props) => (
  <BannerBoundary>
    <BannerContent {...props} />
  </BannerBoundary>
);

export default InDevelopmentBanner;
